import { ReactNode, useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import '../styles/support-notification-ux.css'

type AdminShellUser = {
  email: string
  role: string
}

type AdminShellProps = {
  children: ReactNode
  title: string
  eyebrow?: string
  description?: string
  actions?: ReactNode
  user: AdminShellUser
  onLogout: () => void
  logoutBusy?: boolean
  environmentLabel?: string
  suppressActiveNav?: boolean
}

type SupportNotification = {
  id: string
  eventType: 'public_ticket' | 'customer_reply'
  reference: string
  customerName: string | null
  createdAt: string
}

type AdminGlobalSearchPayload = {
  query: string
  orders: Array<{
    orderId: string
    customerName: string
    suburb: string
    deliveryDay: 'saturday' | 'sunday' | null
    fulfilmentStatus: string | null
    createdAt: string
    href: string
  }>
  customers: Array<{
    customerKey: string
    name: string
    email: string
    phone: string
    latestOrderId: string | null
    href: string
  }>
  supportTickets: Array<{
    reference: string
    subject: string
    requester: string
    status: string
    linkedOrderId: string | null
    href: string
  }>
  pages: Array<{
    label: string
    path: string
    href: string
  }>
}

type AdminGlobalSearchStatus = 'idle' | 'searching' | 'results' | 'error'

type NavItem = { label: string; path: string; icon: AdminIconName }
type NavGroup = { label: string; items: NavItem[] }
type AdminIconName =
  | 'home' | 'store' | 'orders' | 'customers' | 'menu' | 'money' | 'discount'
  | 'support' | 'media' | 'reviews' | 'email' | 'reports' | 'diagnostics' | 'settings' | 'security'
  | 'menu-toggle' | 'close' | 'external' | 'logout' | 'search' | 'bell'

const NAV_GROUPS: NavGroup[] = [
  { label: 'Overview', items: [
    { label: 'Dashboard', path: '/admin', icon: 'home' },
    { label: 'Store Status', path: '/admin/store-status', icon: 'store' },
  ] },
  { label: 'Operations', items: [
    { label: 'Orders', path: '/admin/orders', icon: 'orders' },
    { label: 'Customers', path: '/admin/customers', icon: 'customers' },
    { label: 'Weekly Menu', path: '/admin/weekly-menu', icon: 'menu' },
    { label: 'Add-ons', path: '/admin/add-ons', icon: 'menu' },
    { label: 'Pricing & Delivery', path: '/admin/pricing-delivery', icon: 'money' },
    { label: 'Discount Codes', path: '/admin/discount-codes', icon: 'discount' },
    { label: 'Support Tickets', path: '/admin/support', icon: 'support' },
  ] },
  { label: 'Content & Comms', items: [
    { label: 'Content & Media', path: '/admin/content-media', icon: 'media' },
    { label: 'Reviews', path: '/admin/reviews', icon: 'reviews' },
    { label: 'Email & Notifications', path: '/admin/email-notifications', icon: 'email' },
    { label: 'Reports', path: '/admin/reports', icon: 'reports' },
  ] },
  { label: 'System', items: [
    { label: 'Diagnostics', path: '/admin/diagnostics', icon: 'diagnostics' },
    { label: 'System Settings', path: '/admin/settings', icon: 'settings' },
    { label: 'Admin & Security', path: '/admin/security', icon: 'security' },
  ] },
]

const SUPPORT_NOTIFICATION_TIME = new Intl.DateTimeFormat('en-NZ', {
  timeZone: 'Pacific/Auckland',
  day: 'numeric',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
})

function notificationWhen(value: string) {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : SUPPORT_NOTIFICATION_TIME.format(date)
}

function normalisePath(pathname: string) {
  const trimmed = pathname.replace(/\/+$/, '')
  return trimmed || '/admin'
}

function isActivePath(currentPath: string, itemPath: string) {
  if (itemPath === '/admin') return currentPath === '/admin'
  return currentPath === itemPath || currentPath.startsWith(`${itemPath}/`)
}

function getFocusable(container: HTMLElement | null) {
  if (!container) return []
  return Array.from(container.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )).filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null)
}

function AdminIcon({ name }: { name: AdminIconName }) {
  const common = {
    width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.9,
    strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true,
  }

  if (name === 'home') return <svg {...common}><path d="M3 11.5 12 4l9 7.5" /><path d="M5.5 10.5V20h13v-9.5" /><path d="M9.5 20v-5h5v5" /></svg>
  if (name === 'store') return <svg {...common}><path d="M4 9h16l-1-5H5L4 9Z" /><path d="M6 9v11h12V9" /><path d="M9 20v-6h6v6" /><path d="M4 9c0 1.3 1 2.3 2.3 2.3S8.7 10.3 8.7 9c0 1.3 1 2.3 2.3 2.3s2.3-1 2.3-2.3c0 1.3 1 2.3 2.3 2.3S18 10.3 18 9" /></svg>
  if (name === 'orders') return <svg {...common}><rect x="5" y="4" width="14" height="16" rx="2" /><path d="M9 8h6M9 12h6M9 16h4" /></svg>
  if (name === 'customers') return <svg {...common}><circle cx="9" cy="8" r="3" /><path d="M3.5 19c.6-3.2 2.4-5 5.5-5s4.9 1.8 5.5 5" /><path d="M15 6.3a3 3 0 0 1 0 5.4M16 14.2c2.4.5 3.9 2.1 4.5 4.8" /></svg>
  if (name === 'menu') return <svg {...common}><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16M8 14h3M8 17h5" /></svg>
  if (name === 'money') return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M15.5 8.5c-.8-.8-1.9-1.2-3.2-1.2-1.8 0-3.2.9-3.2 2.3 0 3.6 6.5 1.5 6.5 5 0 1.5-1.5 2.5-3.5 2.5-1.4 0-2.8-.5-3.7-1.5M12 5.5v13" /></svg>
  if (name === 'discount') return <svg {...common}><path d="M20 13 13 20 4 11V4h7l9 9Z" /><circle cx="8.3" cy="8.3" r="1.2" /></svg>
  if (name === 'support') return <svg {...common}><path d="M4 5h16v12H9l-5 4V5Z" /><path d="M8 9h8M8 13h5" /></svg>
  if (name === 'media') return <svg {...common}><rect x="3.5" y="4" width="17" height="16" rx="2" /><circle cx="9" cy="9" r="1.8" /><path d="m5.5 17 4.2-4 3.2 3 2.3-2.1 3.3 3.1" /></svg>
  if (name === 'reviews') return <svg {...common}><path d="M4 5h16v12H9l-5 4V5Z" /><path d="m12 8 .9 1.8 2 .3-1.5 1.4.4 2-1.8-.9-1.8.9.4-2-1.5-1.4 2-.3L12 8Z" /></svg>
  if (name === 'email') return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m4 7 8 6 8-6" /></svg>
  if (name === 'reports') return <svg {...common}><path d="M5 20V10M12 20V4M19 20v-7" /><path d="M3 20h18" /></svg>
  if (name === 'diagnostics') return <svg {...common}><path d="M4 12h3l2-5 4 10 2-5h5" /><rect x="3" y="4" width="18" height="16" rx="2" /></svg>
  if (name === 'settings') return <svg {...common}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1A1.7 1.7 0 0 0 9 4.6 1.7 1.7 0 0 0 10 3V2.8h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z" /></svg>
  if (name === 'security') return <svg {...common}><path d="M12 3 5 6v5c0 4.5 2.8 8 7 10 4.2-2 7-5.5 7-10V6l-7-3Z" /><path d="m9 12 2 2 4-4" /></svg>
  if (name === 'menu-toggle') return <svg {...common}><path d="M4 7h16M4 12h16M4 17h16" /></svg>
  if (name === 'close') return <svg {...common}><path d="m6 6 12 12M18 6 6 18" /></svg>
  if (name === 'external') return <svg {...common}><path d="M14 5h5v5M19 5l-8 8" /><path d="M18 13v6H5V6h6" /></svg>
  if (name === 'logout') return <svg {...common}><path d="M10 5H5v14h5M14 8l4 4-4 4M8 12h10" /></svg>
  if (name === 'search') return <svg {...common}><circle cx="11" cy="11" r="6" /><path d="m16 16 4 4" /></svg>
  if (name === 'bell') return <svg {...common}><path d="M18 9a6 6 0 0 0-12 0c0 7-3 7-3 7h18s-3 0-3-7" /><path d="M10 20h4" /></svg>
  return null
}

export default function AdminShell({
  children,
  title,
  eyebrow = 'BOSS ZONE',
  description,
  actions,
  user,
  onLogout,
  logoutBusy = false,
  environmentLabel,
  suppressActiveNav = false,
}: AdminShellProps) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [compactNav, setCompactNav] = useState(() => window.matchMedia('(max-width: 900px)').matches)
  const [notificationOpen, setNotificationOpen] = useState(false)
  const [supportUnread, setSupportUnread] = useState(0)
  const [supportNotifications, setSupportNotifications] = useState<SupportNotification[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [searchStatus, setSearchStatus] = useState<AdminGlobalSearchStatus>('idle')
  const [searchResults, setSearchResults] = useState<AdminGlobalSearchPayload | null>(null)
  const [now, setNow] = useState(() => new Date())
  const currentPath = useMemo(() => normalisePath(window.location.pathname), [])
  const initials = user.email.slice(0, 2).toUpperCase()
  const sidebarRef = useRef<HTMLElement>(null)
  const navCloseRef = useRef<HTMLButtonElement>(null)
  const navReturnFocusRef = useRef<HTMLElement | null>(null)
  const searchFormRef = useRef<HTMLFormElement>(null)
  const searchAbortRef = useRef<AbortController | null>(null)

  const dateLabel = useMemo(() => new Intl.DateTimeFormat('en-NZ', {
    timeZone: 'Pacific/Auckland', weekday: 'short', day: 'numeric', month: 'short',
  }).format(now), [now])
  const timeLabel = useMemo(() => new Intl.DateTimeFormat('en-NZ', {
    timeZone: 'Pacific/Auckland', hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(now), [now])

  const searchResultCount = useMemo(() => {
    if (!searchResults) return 0
    return searchResults.orders.length
      + searchResults.customers.length
      + searchResults.supportTickets.length
      + searchResults.pages.length
  }, [searchResults])

  useEffect(() => {
    const media = window.matchMedia('(max-width: 900px)')
    const onChange = () => {
      setCompactNav(media.matches)
      if (!media.matches) setMobileNavOpen(false)
    }
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000)
    return () => window.clearInterval(timer)
  }, [])

  useEffect(() => {
    let active = true
    const refresh = async () => {
      try {
        const response = await fetch('/api/admin/support/notifications', { credentials: 'same-origin', cache: 'no-store' })
        if (!response.ok) return
        const payload = await response.json() as { unreadNotifications?: number; notifications?: SupportNotification[] }
        if (!active) return
        setSupportUnread(Math.max(0, Number(payload.unreadNotifications ?? 0)))
        setSupportNotifications(Array.isArray(payload.notifications) ? payload.notifications : [])
      } catch {
        // Support notification UX is non-blocking for the rest of Boss Zone.
      }
    }
    void refresh()
    const timer = window.setInterval(() => void refresh(), 30_000)
    const onFocus = () => void refresh()
    const onChanged = () => void refresh()
    window.addEventListener('focus', onFocus)
    window.addEventListener('support-notifications-changed', onChanged)
    return () => {
      active = false
      window.clearInterval(timer)
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('support-notifications-changed', onChanged)
    }
  }, [])

  useEffect(() => () => searchAbortRef.current?.abort(), [])

  useEffect(() => {
    if (searchStatus === 'idle') return
    const onPointerDown = (event: PointerEvent) => {
      if (searchFormRef.current && !searchFormRef.current.contains(event.target as Node)) {
        setSearchStatus('idle')
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSearchStatus('idle')
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [searchStatus])

  async function submitGlobalSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const query = searchQuery.trim().slice(0, 80)
    if (query.length < 2) {
      searchAbortRef.current?.abort()
      setSearchResults(null)
      setSearchStatus('idle')
      return
    }

    searchAbortRef.current?.abort()
    const controller = new AbortController()
    searchAbortRef.current = controller
    setSearchStatus('searching')
    setSearchResults(null)

    try {
      const response = await fetch(`/api/admin/search?q=${encodeURIComponent(query)}`, {
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
      })
      if (response.status === 401) {
        const next = `${window.location.pathname}${window.location.search}`
        window.location.replace(`/admin/login?next=${encodeURIComponent(next)}`)
        return
      }
      if (!response.ok) throw new Error('global search unavailable')
      const payload = await response.json() as AdminGlobalSearchPayload
      if (controller.signal.aborted) return
      setSearchResults(payload)
      setSearchStatus('results')
    } catch (error) {
      if (controller.signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) return
      setSearchResults(null)
      setSearchStatus('error')
    }
  }

  function changeGlobalSearch(value: string) {
    searchAbortRef.current?.abort()
    setSearchQuery(value)
    setSearchResults(null)
    setSearchStatus('idle')
  }

  function openMobileNav() {
    navReturnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    setMobileNavOpen(true)
  }

  function closeMobileNav(restoreFocus = true) {
    setMobileNavOpen(false)
    if (restoreFocus) window.requestAnimationFrame(() => navReturnFocusRef.current?.focus())
  }

  useEffect(() => {
    if (!mobileNavOpen || !compactNav) return
    document.body.classList.add('admin-nav-open')
    window.requestAnimationFrame(() => navCloseRef.current?.focus())

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        closeMobileNav(true)
        return
      }
      if (event.key !== 'Tab') return
      const focusable = getFocusable(sidebarRef.current)
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.classList.remove('admin-nav-open')
    }
  }, [mobileNavOpen, compactNav])

  const sidebarTabIndex = compactNav && !mobileNavOpen ? -1 : undefined

  return (
    <div className="admin-shell">
      <aside
        id="boss-zone-navigation"
        ref={sidebarRef}
        className={`admin-shell__sidebar ${mobileNavOpen ? 'is-open' : ''}`}
        aria-label="Boss Zone navigation"
        aria-hidden={compactNav && !mobileNavOpen ? true : undefined}
      >
        <div className="admin-shell__brand">
          <a href="/admin" tabIndex={sidebarTabIndex} className="admin-shell__brand-link" aria-label="Sugar Gremlin Boss Zone home">
            <img src="/assets/brand/sg-horizontal-logo-approved.png" alt="Sugar Gremlin" />
            <span>BOSS ZONE</span>
          </a>
          <button ref={navCloseRef} className="admin-shell__nav-close" type="button" onClick={() => closeMobileNav(true)} aria-label="Close navigation">
            <AdminIcon name="close" />
          </button>
        </div>

        <nav className="admin-shell__nav">
          {NAV_GROUPS.map((group) => (
            <div className="admin-shell__nav-group" key={group.label}>
              <span className="admin-shell__nav-label">{group.label}</span>
              {group.items.map((item) => {
                const active = !suppressActiveNav && isActivePath(currentPath, item.path)
                return (
                  <a
                    className={`admin-shell__nav-item ${active ? 'is-active' : ''}`}
                    href={item.path}
                    tabIndex={sidebarTabIndex}
                    aria-current={active ? 'page' : undefined}
                    key={item.path}
                    onClick={() => setMobileNavOpen(false)}
                  >
                    <span className="admin-shell__nav-icon"><AdminIcon name={item.icon} /></span>
                    <span className="admin-shell__nav-item-label">{item.label}</span>
                    {item.path === '/admin/support' && supportUnread > 0
                      ? <span className="admin-shell__nav-badge" aria-label={`${supportUnread} unread Support notifications`}>{supportUnread > 99 ? '99+' : supportUnread}</span>
                      : null}
                  </a>
                )
              })}
            </div>
          ))}
        </nav>

        <div className="admin-shell__account">
          {environmentLabel && <span className="admin-shell__environment-chip">{environmentLabel}</span>}
          <div className="admin-shell__account-identity">
            <span className="admin-shell__avatar" aria-hidden="true">{initials}</span>
            <span className="admin-shell__user-copy">
              <strong>{user.email}</strong>
              <span>{user.role}</span>
            </span>
          </div>
          <button className="admin-shell__logout" tabIndex={sidebarTabIndex} type="button" onClick={onLogout} disabled={logoutBusy}>
            <AdminIcon name="logout" />
            <span>{logoutBusy ? 'Signing out…' : 'Logout'}</span>
          </button>
        </div>
      </aside>

      {mobileNavOpen && <button className="admin-shell__scrim" type="button" aria-label="Close navigation" onClick={() => closeMobileNav(true)} />}

      <div className="admin-shell__main">
        <header className="admin-shell__utility">
          <div className="admin-shell__utility-left">
            <button
              className="admin-shell__menu-button"
              type="button"
              onClick={openMobileNav}
              aria-label="Open navigation"
              aria-expanded={mobileNavOpen}
              aria-controls="boss-zone-navigation"
            >
              <AdminIcon name="menu-toggle" />
            </button>

            <form
              ref={searchFormRef}
              className="admin-shell__global-search"
              role="search"
              onSubmit={submitGlobalSearch}
            >
              <AdminIcon name="search" />
              <input
                type="search"
                value={searchQuery}
                maxLength={80}
                onChange={(event) => changeGlobalSearch(event.target.value)}
                placeholder="Search Boss Zone"
                aria-label="Search Boss Zone"
                aria-expanded={searchStatus !== 'idle'}
                aria-controls="boss-zone-global-search-results"
                autoComplete="off"
              />
              {searchStatus !== 'idle' && (
                <div
                  id="boss-zone-global-search-results"
                  className="admin-shell__search-popover"
                  role="status"
                  aria-live="polite"
                >
                  {searchStatus === 'searching' ? (
                    <div className="admin-shell__search-state">Searching…</div>
                  ) : searchStatus === 'error' ? (
                    <div className="admin-shell__search-state is-error">Search could not be completed. Try again.</div>
                  ) : searchResults && searchResultCount === 0 ? (
                    <div className="admin-shell__search-state">No Boss Zone results for “{searchResults.query}”.</div>
                  ) : searchResults ? (
                    <div className="admin-shell__search-groups">
                      {searchResults.orders.length ? (
                        <section className="admin-shell__search-group" aria-label="Order results">
                          <strong>Orders</strong>
                          {searchResults.orders.map((result) => (
                            <a href={result.href} key={result.orderId}>
                              <b>{result.orderId} · {result.customerName}</b>
                              <span>{[result.suburb, result.deliveryDay, result.fulfilmentStatus].filter(Boolean).join(' · ') || 'Order'}</span>
                            </a>
                          ))}
                        </section>
                      ) : null}
                      {searchResults.customers.length ? (
                        <section className="admin-shell__search-group" aria-label="Customer results">
                          <strong>Customers</strong>
                          {searchResults.customers.map((result) => (
                            <a href={result.href} key={result.customerKey}>
                              <b>{result.name}</b>
                              <span>{[result.email, result.phone].filter(Boolean).join(' · ')}</span>
                            </a>
                          ))}
                        </section>
                      ) : null}
                      {searchResults.supportTickets.length ? (
                        <section className="admin-shell__search-group" aria-label="Support results">
                          <strong>Support</strong>
                          {searchResults.supportTickets.map((result) => (
                            <a href={result.href} key={result.reference}>
                              <b>{result.reference} · {result.subject}</b>
                              <span>{[result.requester, result.status, result.linkedOrderId].filter(Boolean).join(' · ')}</span>
                            </a>
                          ))}
                        </section>
                      ) : null}
                      {searchResults.pages.length ? (
                        <section className="admin-shell__search-group" aria-label="Boss Zone page results">
                          <strong>Boss Zone pages</strong>
                          {searchResults.pages.map((result) => (
                            <a href={result.href} key={result.path}>
                              <b>{result.label}</b>
                              <span>{result.path}</span>
                            </a>
                          ))}
                        </section>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )}
            </form>
          </div>

          <div className="admin-shell__utility-right">
            <div className="admin-shell__datetime" aria-label={`${dateLabel}, ${timeLabel} New Zealand time`}>
              <strong>{timeLabel}</strong>
              <span>{dateLabel}</span>
            </div>

            <div className="admin-shell__notifications">
              <button
                className="admin-shell__notification-button"
                type="button"
                aria-label={supportUnread ? `Notifications, ${supportUnread} unread Support ${supportUnread === 1 ? 'notification' : 'notifications'}` : 'Notifications, none unread'}
                aria-expanded={notificationOpen}
                aria-controls="boss-zone-notifications"
                onClick={() => setNotificationOpen((open) => !open)}
              >
                <AdminIcon name="bell" />
                {supportUnread > 0 && <span className="admin-shell__notification-count" aria-hidden="true">{supportUnread > 99 ? '99+' : supportUnread}</span>}
              </button>
              {notificationOpen && (
                <div id="boss-zone-notifications" className="admin-shell__notification-popover" role="dialog" aria-label="Unread Support notifications">
                  <strong>Notifications</strong>
                  {supportNotifications.length > 0
                    ? <div className="admin-shell__notification-list">
                        {supportNotifications.map((notification) => (
                          <a className="admin-shell__notification-item" href={`/admin/support?ticket=${encodeURIComponent(notification.reference)}`} key={notification.id}>
                            <strong>{notification.eventType === 'customer_reply' ? 'Customer replied' : 'New support ticket'}</strong>
                            <span>{notification.reference}{notification.customerName ? ` · ${notification.customerName}` : ''}</span>
                            <small>{notificationWhen(notification.createdAt)}</small>
                          </a>
                        ))}
                      </div>
                    : <span className="admin-shell__notification-empty">No unread Support notifications.</span>}
                </div>
              )}
            </div>

            <a className="admin-shell__site-link" href="/" target="_blank" rel="noreferrer">
              View site <AdminIcon name="external" />
            </a>
          </div>
        </header>

        <main className="admin-shell__workspace">
          <section className="admin-shell__page-header">
            <div>
              <span className="admin-shell__eyebrow">{eyebrow}</span>
              <h1>{title}</h1>
              {description && <p>{description}</p>}
            </div>
            {actions && <div className="admin-shell__page-actions">{actions}</div>}
          </section>
          {children}
        </main>
      </div>
    </div>
  )
}

export { AdminIcon }
export type { AdminShellUser }
